import { normalize, denormalize, schema } from 'normalizr';
import { defaultsDeep, uniq } from 'lodash';
import compareDesc from 'date-fns/compareDesc';
import _get from 'lodash/get';

// TODO: Update names of these functions, rename all 'jobs' to 'matches'
// and update 'cancelStatus' to cancel-job.
import {
  getJobs,
  // Actions
  acceptJob,
  rejectJob,
  completeJob,
  cancelJob,
  closeJob,
  hireJob,
  askLater,
  calledConsumer,
  seeJob,
} from '@homeservices/api-kit/proreferral/jobs/v5';

import {
  cancelStatus,
} from '@homeservices/api-kit/workorder/proreferral';
import { ALL_MATCHES } from '@/constants/match-statuses';

import { createJob } from '@/transformations/job';
import { createProJob } from '@/transformations/pro-job';
import publicProfiles from '@/store/common-modules/public-profiles';

const subtaskSchema = new schema.Entity('subtask');
const matchSchema = new schema.Entity('matches', {}, {
  idAttribute: (value, parent) => `${parent.id}-${value.skills_profile}`,
});

const jobSchema = new schema.Entity('jobs', {
  matches: [matchSchema],
  match: matchSchema,
  subtask: subtaskSchema,
});

const defaultsDeepReducer = (acc, next) => ({
  ...acc,
  [next.id]: defaultsDeep(next, acc[next.id] || {}),
});

// TODO: update action maping after api-kit name changes.
export const jobActions = {
  accept: acceptJob,
  reject: rejectJob,
  complete: completeJob,
  cancel: cancelJob,
  'cancel-all': cancelStatus,
  close: closeJob,
  hire: hireJob,
  'ask-later': askLater,
  'called-consumer': calledConsumer,
  see: seeJob,
};

export default {
  state () {
    return {
      jobs: {},
      matches: {},
      subtask: {},
      meta: {},
    };
  },
  modules: {
    commonPublicProfiles: publicProfiles,
  },
  getters: {
    // --------------------------------
    // TODO (Volkan): Think about this
    canCustomerBeMessaged: () => (customer) => {
      return (customer.available_contact_methods || []).some(val => val === 'message');
    },
    canCustomerBeCalled: () => (customer) => {
      return (customer.available_contact_methods || []).some(val => val === 'phone');
    },
    // -------------------------------
    getMatch: (state, getters) => (jobId, skillsProfileId) => {
      const job = getters.getJob(jobId);
      return job.matches.find(match => match.skills_profile === parseInt(skillsProfileId, 10));
    },
    getJob: state => (id) => {
      return denormalize(state.jobs[id], jobSchema, state);
    },
    getJobs: (state, getters) => (ids = Object.keys(state.jobs)) => {
      return ids.map(getters.getJob);
    },
    getJobsByStatusGroup: (state, getters) => (group) => {
      const jobs = getters.getJobs();
      // If there are no statuses, don't filter
      if (!group.statuses.length) { return jobs; }

      // When pro gets a a job, they only have a single match
      // so 'jobs.matches' only filters through 1 match.
      return jobs.filter(
        job => job.matches
          .some(match => group.statuses.some(status => !status || match.status.name === status))
      );
    },
    getMetaByStatusGroup: (state, getters) => (group) => {
      const groupMeta = {
        total: 0,
        loading: false,
        count: getters.getJobsByStatusGroup(group).length,
        ...state.meta[group.key],
      };

      return Object.freeze({
        ...groupMeta,
        hasMore: groupMeta.total > groupMeta.count,
        loadingMore: groupMeta.loading && groupMeta.count,
        initialLoading: groupMeta.loading && !groupMeta.count,
      });
    },
    getStructuredDataByStatusGroup: (state, getters) => group => ({
      jobs: getters.getJobsByStatusGroup(group),
      meta: getters.getMetaByStatusGroup(group),
    }),
    sortJobs: () => jobs => (property) => {
      return jobs.sort((a, b) => {
        return compareDesc(
          new Date(_get(a, property)),
          new Date(_get(b, property))
        );
      });
    },
  },
  actions: {
    transformJob ({ getters, dispatch, rootGetters }, job) {
      const userId = rootGetters['user/userId'];
      const isPro = rootGetters['user/isPro'];
      if (isPro) {
        return createProJob({ ...job, proId: userId });
      }

      job.matches.forEach((match) => {
        if (!getters.getPublicProfileLoading(match.skills_profile)) {
          dispatch('fetchPublicProfile', { ids: match.skills_profile, jobId: job.id });
        }
      });
      return createJob(job);
    },
    async fetchFiles ({ commit, dispatch, rootGetters }, { id }) {
      const isPro = rootGetters['user/isPro'];
      // TODO: Fix files/filesStore discrepancy after consumer store refactoring
      const response = await dispatch(`${isPro ? 'prohub/filesStore' : 'consumer/files'}/getJobFiles`, { id }, { root: true });
      if (response.data.count) {
        commit('addFilesToJob', {
          id,
          fileIds: response.data.results.map(file => file.id),
        });
      }
    },
    async fetchJobs (
      { commit, dispatch, rootGetters },
      {
        group = ALL_MATCHES,
        metaKey = group.key,
        params = {},
        cancelToken = null,
      } = { group: ALL_MATCHES, params: {} }
    ) {
      const userId = rootGetters['user/userId'];
      const isPro = rootGetters['user/isPro'];

      const paramsToSend = {
        [isPro ? 'pro' : 'consumer']: userId,
        // TODO: This is a ridiculus filtering, this needs be removed once Pro is
        // TODO: restricted to receive 'unresponsive' leads
        // -----
        // TODO: Possible refactoring:
        // TODO: match status field is only available for pros, not for consumers.
        match_status: isPro ? group.statuses.filter(name => name !== 'unresponsive').join() : '',
        ...params,
      };

      if (!paramsToSend.match_status) {
        delete paramsToSend.match_status;
      }

      commit('updateMeta', { key: metaKey, loading: true });
      const response = await getJobs(paramsToSend, cancelToken);
      const { data } = response;
      const jobs = await Promise.all(data.results.map(job => dispatch('transformJob', job)));
      const { entities } = normalize(jobs, [jobSchema]);
      commit('updateMeta', { key: metaKey, total: data.total, loading: false });
      commit('updateState', { entities });
      return response;
    },
    async loadMore ({ dispatch, getters }, { group = ALL_MATCHES, params = {} }) {
      const groupMeta = getters.getMetaByStatusGroup(group);

      await dispatch('fetchJobs', {
        group,
        params: {
          ...params,
          from: groupMeta.count,
        },
      });
    },
    async action ({ dispatch }, { name, id, ...params }) {
      try {
        await jobActions[name](id, params);
        // TODO: Make sure this call is not necessary
        // (craetes double calls on certain pages)
        await dispatch('fetchJob', { id });
      } catch (err) {
        // TODO(volkan): figure out if this is necessary to have "console.error"
        console.error(err); // eslint-disable-line no-console
      }
    },
    async cancelJobConsumer ({ dispatch }, { id, reason, jobId }) {
      try {
        await cancelStatus(id, { reason });
        await dispatch('dashboard/workOrder/fetchWorkOrder', { jobId }, { root: true });
      } catch (err) {
        console.error(err); // eslint-disable-line no-console
      }
    },
  },
  mutations: {
    updateState (state, { entities = {} }) {
      const newEntities = { ...entities };

      // This is important: (same for customer)
      // If there no existing job, create it. But if there is an existing job, use
      // new job as base and add keys that are not on new job from existing job.
      // This is different than plain merging so be careful when updating this logic
      const defaultsDeepKeys = ['jobs', 'customer'];
      defaultsDeepKeys.forEach((key) => {
        if (newEntities[key]) {
          newEntities[key] = Object.values(newEntities[key])
            .reduce(defaultsDeepReducer, state[key]);
        }
      });

      Object.keys(newEntities).forEach((entityName) => {
        const entity = newEntities[entityName];

        state[entityName] = {
          ...state[entityName],
          ...entity,
        };
      });
    },
    updateMeta (state, { key, total, loading }) {
      if (key) {
        const groupMeta = state.meta[key] || {};
        if (total !== undefined) { groupMeta.total = total; }
        if (loading !== undefined) { groupMeta.loading = loading; }

        state.meta = {
          ...state.meta,
          [key]: groupMeta,
        };
      }
    },
    // TODO: (fix this)
    addFilesToJob (state, { id, fileIds }) {
      const existingJob = state.jobs[id];
      const uniqueIds = uniq([...fileIds, ...existingJob.files]);
      existingJob.files = uniqueIds;

      state.jobs = {
        ...state.jobs,
        [id]: existingJob,
      };
    },
  },
};
